<script setup lang="ts">
    'use strict'

    import { ref, computed, watch, nextTick } from 'vue'
    import { useStorage } from '@vueuse/core'
    import html2canvas from 'html2canvas'
    import BarChart from './BarChart'
    import BarChartStacked from './BarChartStacked'
    import LineChart from './LineChart'
    import BoxPlotChart from './BoxPlotChart'

    import type { Kennzahl, DashboardValue } from '@/js/composables/chart/types'
    import { buildSimpleChartData } from '@/js/composables/chart/useChartDataSimple'
    import { buildStackedChartData, buildSubKennzahlChartData } from '@/js/composables/chart/useChartDataStacked'
    import { buildBarChartBarWithLine } from '@/js/composables/chart/useChartDataBarWithLine'
    import { buildBarChartParallel } from '@/js/composables/chart/useChartDataBarChartParallel'
    import { buildBundeslandChartData } from '@/js/composables/chart/useChartDataBundesland'
    import { buildAggregatedMapChartData } from '@/js/composables/chart/useChartDataAggregatedMap'
    import { buildBoxPlotChartData } from '@/js/composables/chart/useChartDataBoxPlot'
    import { buildYearlyAggregateChartData } from '@/js/composables/chart/useChartDataYearlyAggregate'
    import { buildSummedSubsChartData } from '@/js/composables/chart/useChartDataSummedSubs'

    const props = withDefaults(
        defineProps<{
            kennzahl: Kennzahl
            dashboardData: DashboardValue[]
            dashboardDataYears: string[]
            storageKey: string
            boxSize: number
            showTitle: boolean
            showTargetValue: boolean
            /** true, wenn dieses Item das 2. von genau 2 Items ist (zentriert es horizontal). */
            centerBox: boolean
        }>(),
        {
            centerBox: false
        }
    )

    const selectedType = useStorage<string>(
        `selected-chart-${props.storageKey}-${props.kennzahl.kennzahlId}`,
        props.kennzahl.types[0]
    )

    const isLoading = ref(false)
    const isDownloading = ref(false)

    /*
    |--------------------------------------------------------------------------
    | Datenpunkte für eine Kennzahl-ID — gefiltert nach Semester und sortiert.
    | Schließt reaktiv über props.dashboardData und props.dashboardDataYears.
    |--------------------------------------------------------------------------
    */
    function getValues(kennzahlId: string): DashboardValue[] {
        return props.dashboardData
            .filter((d) => d.kennzahlId === kennzahlId && props.dashboardDataYears.includes(d.semester))
            .sort((a, b) => a.year - b.year)
    }

    const dataCollection = computed<any>(() => {
        const years = props.dashboardDataYears
        const strategy = props.kennzahl.dataStrategy ?? 'simple'

        switch (strategy) {
            case 'bundesland':
                return buildBundeslandChartData(props.kennzahl, getValues, years)
            case 'aggregatedMap':
                return buildAggregatedMapChartData(props.kennzahl, getValues, years)
            case 'stacked':
                return selectedType.value === 'BarChartStacked'
                    ? buildStackedChartData(props.kennzahl, getValues, years)
                    : buildSubKennzahlChartData(props.kennzahl, getValues, years)
            case 'mixedAxis':
                return selectedType.value === 'BarChartStacked'
                    ? buildBarChartBarWithLine(props.kennzahl, getValues, years)
                    : buildBarChartParallel(props.kennzahl, getValues, years)
            case 'boxplot':
                return buildBoxPlotChartData(props.kennzahl, getValues, years)
            case 'yearlyAggregate':
                return buildYearlyAggregateChartData(
                    props.kennzahl,
                    props.dashboardData,
                    selectedType.value,
                    Math.ceil(props.dashboardDataYears.length / 2)
                )
            case 'summedSubs':
                return buildSummedSubsChartData(props.kennzahl, getValues, years)
            default:
                return buildSimpleChartData(props.kennzahl, getValues, years, selectedType.value)
        }
    })

    watch(
        () => [props.dashboardData, props.dashboardDataYears],
        async () => {
            isLoading.value = true
            await nextTick()
            isLoading.value = false
        },
        { deep: true }
    )

    /*
    |--------------------------------------------------------------------------
    | Typ-Selektion
    |--------------------------------------------------------------------------
    */
    function setSelectedChartType(type: string) {
        selectedType.value = type
    }

    function isMenuVisible() {
        return dataCollection.value && !isDownloading.value
    }

    function hasTypeBoxPlotChart() {
        return dataCollection.value && props.kennzahl.types.includes('BoxPlotChart')
    }

    function hasTypeLineChart() {
        return dataCollection.value && props.kennzahl.types.includes('LineChart')
    }

    function hasTypeBarChart() {
        return dataCollection.value && props.kennzahl.types.includes('BarChart')
    }

    function hasTypeBarChartStacked() {
        return dataCollection.value && props.kennzahl.types.includes('BarChartStacked')
    }

    function isTypeBoxPlotChartEnabled() {
        return selectedType.value === 'BoxPlotChart'
    }

    function isTypeLineChartEnabled() {
        return selectedType.value === 'LineChart'
    }

    function isTypeBarChartEnabled() {
        return selectedType.value === 'BarChart'
    }

    function isTypeBarChartStackedEnabled() {
        return selectedType.value === 'BarChartStacked'
    }

    // "Anzahl …"-Kennzahlen haben ganzzahlige Werte — y-Achse soll keine Komma-Ticks zeigen.
    function isCountKennzahl(): boolean {
        return props.kennzahl.kennzahlTitle.trim().startsWith('Anzahl')
    }

    /*
    |--------------------------------------------------------------------------
    | Styles & Download
    |--------------------------------------------------------------------------
    */
    const boxStyle = computed(() => `height:${props.boxSize}px; width: ${props.boxSize}px;`)

    async function downloadChart() {
        const id = props.kennzahl.kennzahlId.replace('.', '').replace('.', '')
        const element = document.querySelector(`#chart-${id}`)

        if (!element) {
            console.error('no element found')
            return
        }
        isDownloading.value = true
        await nextTick()

        html2canvas(element as HTMLElement).then((canvas) => {
            let type = ''
            switch (selectedType.value) {
                case 'LineChart':
                    type = '_Liniendiagramm'
                    break
                case 'BarChart':
                    type = '_Säulendiagramm'
                    break
                case 'BoxPlotChart':
                    type = '_Boxplotdiagramm'
                    break
                case 'BarChartStacked':
                    type = '_gestapeltes_Säulendiagramm'
                    break
            }

            const link = document.createElement('a')
            link.download = `${props.kennzahl.kennzahlId}_${props.kennzahl.kennzahlTitle
                .replace('&shy;', '')
                .replace('&shy;', '')}${type}.png`
            link.href = canvas.toDataURL('image/png')
            link.click()
            isDownloading.value = false
        })
    }
</script>

<template>
    <div
        :id="`chart-${kennzahl.kennzahlId.replace('.', '').replace('.', '')}`"
        class="d-flex flex-column"
        :class="{ 'mx-auto': centerBox }"
        :style="`max-width: ${props.boxSize + 4}px;`"
    >
        <!-- eslint-disable vue/no-v-html -->
        <div
            v-if="showTitle"
            class="font-weight-bold flex-grow-1 pr-1"
            v-html="`${kennzahl.kennzahlId}: ${kennzahl.kennzahlTitle}`"
        />
        <div>
            <span v-if="showTargetValue || true === kennzahl.showTargetValue" style="font-size: 13px">
                Der SOLL-Wert für SoSe 2026 ist {{ kennzahl.targetValue }}.
            </span>
            <v-menu
                v-if="isMenuVisible()"
                close-on-content-click
                :offset="[4, 0]"
                location="bottom end"
                scroll-strategy="close"
            >
                <template v-slot:activator="{ props: menuProps }">
                    <v-btn
                        :color="false"
                        icon="mdi-dots-vertical"
                        v-bind="menuProps"
                        density="compact"
                        :rounded="false"
                        class="ml-2 float-right"
                    />
                </template>
                <v-list>
                    <v-list-item
                        v-if="hasTypeLineChart()"
                        :active="isTypeLineChartEnabled()"
                        @click="setSelectedChartType('LineChart')"
                    >
                        <v-list-item-title>Liniendiagramm</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                        v-if="hasTypeBarChart()"
                        :active="isTypeBarChartEnabled()"
                        @click="setSelectedChartType('BarChart')"
                    >
                        <v-list-item-title>Säulendiagramm</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                        v-if="hasTypeBarChartStacked()"
                        :active="isTypeBarChartStackedEnabled()"
                        @click="setSelectedChartType('BarChartStacked')"
                    >
                        <v-list-item-title>gestapeltes Säulendiagramm</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                        v-if="hasTypeBoxPlotChart()"
                        id="boxplot-dropdown-item"
                        :active="isTypeBoxPlotChartEnabled()"
                        @click="setSelectedChartType('BoxPlotChart')"
                    >
                        <v-list-item-title>Boxplot</v-list-item-title>
                    </v-list-item>
                    <v-divider />
                    <v-list-item @click="downloadChart()">
                        <v-list-item-title>Download</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
        </div>
        <div :class="`chart-${kennzahl.kennzahlId.replace('.', '').replace('.', '')} pr-1`">
            <div
                v-if="isLoading"
                class="bg-lightgrey d-flex justify-content-center align-items-center"
                :style="boxStyle"
            >
                <p class="p-2">Wird berechnet.</p>
            </div>
            <div
                v-else-if="!dataCollection"
                class="bg-grey-lighten-4 d-flex justify-center align-center"
                :style="boxStyle"
            >
                <p class="p-2">Daten folgen in Kürze...</p>
            </div>
            <BarChartStacked
                v-else-if="isTypeBarChartStackedEnabled()"
                :chart-data="dataCollection"
                :x-axes-label-string="kennzahl.xAxesLabelString"
                :y-axes-label-string="kennzahl.yAxesLabelString"
                :y-axes-label-string-two="kennzahl.yAxesLabelStringTwo"
                :integer-only="isCountKennzahl()"
                :style="boxStyle"
            />
            <BarChart
                v-else-if="isTypeBarChartEnabled()"
                :chart-data="dataCollection"
                :x-axes-label-string="kennzahl.xAxesLabelString"
                :y-axes-label-string="kennzahl.yAxesLabelString"
                :integer-only="isCountKennzahl()"
                :style="boxStyle"
            />
            <LineChart
                v-else-if="isTypeLineChartEnabled()"
                :chart-data="dataCollection"
                :x-axes-label-string="kennzahl.xAxesLabelString"
                :y-axes-label-string="kennzahl.yAxesLabelString"
                :show-legend="kennzahl.showLegend === true"
                :integer-only="isCountKennzahl()"
                :style="boxStyle"
            />
            <BoxPlotChart
                v-else-if="isTypeBoxPlotChartEnabled()"
                :chart-data="dataCollection"
                :x-axes-label-string="kennzahl.xAxesLabelString"
                :y-axes-label-string="kennzahl.yAxesLabelString"
                :style="boxStyle"
            />
            <div v-else class="bg-lightgrey d-flex justify-content-center align-items-center" :style="boxStyle">
                <p class="p-2">Demnächst verfügbar.</p>
            </div>
        </div>
    </div>
</template>

<style scoped></style>
